import { db } from '../config/firebase';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { SOP } from '../types/sop';
import { sopCache } from './sopCache';

/**
 * Service for handling SOP flag operations
 */
class SOPFlagService {
  /**
   * Flag an SOP for review
   * @param sopId - The ID of the SOP to flag
   * @param reason - The reason for flagging
   * @param userName - The name of the user flagging the SOP
   * @returns Promise<void>
   */
  async flagSOP(sopId: string, reason: string, userName: string): Promise<void> {
    if (!sopId || !reason.trim()) {
      throw new Error('SOP ID and reason are required');
    }
    
    try {
      const sopRef = doc(db, 'sops', sopId);
      const sopDoc = await getDoc(sopRef);
      
      if (!sopDoc.exists()) {
        throw new Error('SOP not found');
      }
      
      await updateDoc(sopRef, {
        flaggedForReview: true,
        flagReason: reason,
        flaggedBy: userName,
        flaggedAt: new Date().toISOString()
      });
      
      // Clear cache to ensure updated data is fetched
      sopCache.clearCache();
    } catch (error) {
      console.error('Error flagging SOP:', error);
      throw new Error('Failed to flag SOP for review');
    }
  }
  
  /**
   * Remove flag from an SOP
   * @param sopId - The ID of the SOP to unflag
   * @returns Promise<void>
   */
  async removeFlagFromSOP(sopId: string): Promise<void> {
    if (!sopId) {
      throw new Error('SOP ID is required');
    }
    
    try {
      const sopRef = doc(db, 'sops', sopId);
      const sopDoc = await getDoc(sopRef);
      
      if (!sopDoc.exists()) {
        throw new Error('SOP not found');
      }
      
      await updateDoc(sopRef, {
        flaggedForReview: false,
        flagReason: null,
        flaggedBy: null,
        flaggedAt: null
      });
      
      // Clear cache to ensure updated data is fetched
      sopCache.clearCache();
    } catch (error) {
      console.error('Error removing flag from SOP:', error);
      throw new Error('Failed to remove flag from SOP');
    }
  }
  
  /**
   * Get all flagged SOPs
   * @returns Promise<SOP[]> - Array of flagged SOPs
   */
  async getFlaggedSOPs(): Promise<SOP[]> {
    try {
      // This would be more efficient with a direct Firestore query,
      // but we'll use the cache for consistency with the rest of the app
      const allSOPs = await sopCache.getSOPs();
      return allSOPs.filter(sop => sop.flaggedForReview === true);
    } catch (error) {
      console.error('Error fetching flagged SOPs:', error);
      throw new Error('Failed to fetch flagged SOPs');
    }
  }
}

export const sopFlagService = new SOPFlagService();
export default sopFlagService;